import type { AiModel } from '@aline/common';
import { AI_MODEL_CATALOG, aiModelNameSchema, legacyUnknownAiModelKey } from '@aline/common';
import { addDays, subDays } from 'date-fns';
import { Apollo } from 'src/gql/generated';

export const UsageRange = {
  CurrentBillingPeriod: 'currentBillingPeriod',
  Custom: 'custom',
  Last7Days: 'last7Days',
  Last30Days: 'last30Days',
  Last90Days: 'last90Days',
} as const;

export type UsageRange = (typeof UsageRange)[keyof typeof UsageRange];

const SOURCE_LABELS = {
  AI_REMINDERS: 'Ai Reminders',
  CHAT: 'Chat',
  CORE_AI_EXTRACTION: 'Core Ai Extraction',
  PARENT_CHILD_RELATIONSHIPS: 'Parent Child Relationships',
  PLAYBOOKS: 'Playbooks',
  REPORTS: 'Reports',
  UNKNOWN: 'Unknown',
} satisfies Record<Apollo.AiUsageSource, string>;

export function getDefaultUsageDateRange() {
  return getUsageDateRange(UsageRange.Last30Days);
}

export function getUsageDateRange(range: UsageRange) {
  const today = new Date();
  const endDate = formatDateInput(today);
  if (range === UsageRange.Last7Days)
    return { endDate, startDate: formatDateInput(subDays(today, 6)) };
  if (range === UsageRange.Last90Days)
    return { endDate, startDate: formatDateInput(subDays(today, 89)) };
  if (range === UsageRange.CurrentBillingPeriod)
    return { endDate, startDate: formatDateInput(subDays(today, 29)) };
  return { endDate, startDate: formatDateInput(subDays(today, 29)) };
}

export function getUsageDateLabels({ endDate, startDate }: { endDate: string; startDate: string }) {
  const labels: string[] = [];
  const start = getDateFromInput(startDate);
  const end = getDateFromInput(endDate);
  if (!start || !end || start > end) return labels;

  for (let date = start; date <= end; date = addDays(date, 1)) {
    labels.push(formatDateInput(date));
  }

  return labels;
}

export function getUsageTimeZoneCopy(timeZone: string) {
  return `Dates are shown in your browser timezone: ${formatTimeZoneLabel(timeZone)}.`;
}

export function getSeriesValue(point: Apollo.AdminAiUsageQuery['adminAiUsage']['series'][number]) {
  return point.totalTokensUsed;
}

export function getUsageModelLabel(model: string) {
  if (model === legacyUnknownAiModelKey) return 'Legacy Unknown';
  if (getIsAiModel(model)) return AI_MODEL_CATALOG[model].label;
  return model;
}

export function getUsageSourceLabel(source: string) {
  if (getIsAiUsageSource(source)) return SOURCE_LABELS[source];
  return source;
}

export function getUsageGroupLabel(args: {
  groupBy: Apollo.AdminAiUsageGroupBy;
  point: Apollo.AdminAiUsageQuery['adminAiUsage']['series'][number];
}) {
  const { groupBy, point } = args;
  if (groupBy === 'MODEL') return getUsageModelLabel(point.groupKey);
  if (groupBy === 'SOURCE') return getUsageSourceLabel(point.groupKey);
  return point.groupLabel;
}

export function formatMicrodollars(microdollars: number) {
  return new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 2,
    style: 'currency',
  }).format(microdollarsToDollars(microdollars));
}

export function formatUsageNumber(value: number) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    notation: 'compact',
  }).format(value);
}

export function formatUsageTimestamp(value: string, timeZone: string) {
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    timeZone,
  }).format(new Date(value));
}

export function microdollarsToDollars(microdollars: number) {
  return microdollars / 1_000_000;
}

export function formatDateInput(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getDateFromInput(value: string) {
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function formatTimeZoneLabel(timeZone: string) {
  return timeZone
    .split('/')
    .map(part => part.replaceAll('_', ' '))
    .join('/');
}

function getIsAiModel(value: string): value is AiModel {
  return aiModelNameSchema.safeParse(value).success;
}

function getIsAiUsageSource(value: string): value is Apollo.AiUsageSource {
  return Object.values(Apollo.AiUsageSource).some(source => source === value);
}
